import { SVGProps, useEffect } from "react";
import { textStyles } from "../../styles/typography";
import { cn } from "../../styles/utils";
import { useListingQuery, usePricingCalculateQuery } from "../../api/hooks";
import { useAtomValue, useSetAtom } from "jotai";
import {
  orderDataAtom,
  paymentResponseAtom,
  orderStatusAtom,
  customerDetailsAtom,
  promoCodeAtom,
} from "./store";
import { PaymentGateway, PaymentStatus, Query } from "@repo/rq-api-public";
import { useBookingStartDateTime } from "../hooks";
import { usePlatformAccountPaymentGatewayGETQuery } from "@repo/rq-api-public/Query";

const isDevEnvironment = ["development", "ondemand"].includes(
  import.meta.env.VITE_SENTRY_ENVIRONMENT
);

export default function PaymentPending() {
  const orderData = useAtomValue(orderDataAtom);
  const customerDetails = useAtomValue(customerDetailsAtom);

  const setOrderStatus = useSetAtom(orderStatusAtom);
  const setPaymentResponse = useSetAtom(paymentResponseAtom);
  const promoCode = useAtomValue(promoCodeAtom);
  const startDateTime = useBookingStartDateTime();

  const { data: listing } = useListingQuery();

  const { data: paymentProvider } = usePlatformAccountPaymentGatewayGETQuery();

  const { data: calculation } = usePricingCalculateQuery({
    promoCode: (!promoCode?.isLoading && promoCode?.submittedCode) || undefined,
    promoCodeStartDateTime: startDateTime,
  });

  const {
    data: paymentInfo,
    isPending,
    isRefetching,
    refetch,
  } = Query.usePaymentsStatusGETQuery({
    guid: orderData?.paymentGuid,
  });

  const { mutate: updatePaymentIntent } =
    Query.usePaymentsIntent__DebugPOSTMutation(
      orderData?.paymentIntentId ?? undefined,
      "payment_intent.succeeded"
    );

  useEffect(() => {
    // for on-demand and local environments we want to manually update the payment intent status to success
    if (
      (paymentProvider?.paymentGateway !== PaymentGateway.Stripe &&
        !isDevEnvironment) ||
      !paymentInfo ||
      isPending
    ) {
      return;
    }
    if (
      [
        PaymentStatus.Intent,
        PaymentStatus.Processing,
        PaymentStatus.CheckoutPending,
      ].includes(paymentInfo.status!)
    ) {
      updatePaymentIntent();
    }
  }, [isPending]);

  useEffect(() => {
    // refetch payment status every second until it is no longer pending
    if (!paymentInfo || isRefetching || isPending) {
      return;
    }
    if (
      [
        PaymentStatus.Intent,
        PaymentStatus.Processing,
        PaymentStatus.CheckoutPending,
      ].includes(paymentInfo.status!)
    ) {
      setTimeout(() => {
        refetch();
      }, 1000);
    }

    if (paymentInfo.status === PaymentStatus.Complete) {
      window.parent.postMessage(
        {
          trackEvent: "payment",
          orderId: orderData?.bookingGuid,
          value: calculation?.totalPrice,
          boatTitle: listing?.title,
          buyerEmail: customerDetails?.email,
          currencySymbol: "$",
        },
        "*"
      );
      setPaymentResponse(paymentInfo);
      setOrderStatus("SUCCESS");
    }

    if (paymentInfo.status === PaymentStatus.Failed) {
      setOrderStatus("PAYMENT_FAILED");
    }
  }, [isRefetching, isPending]);

  return (
    <div className="widget-payment-pending flex flex-col items-center p-10">
      <IconLoading className="widget-payment-pending__icon mb-4" />
      <div
        className={cn(
          textStyles.h2,
          "widget-payment-pending__heading text-blue-dark mb-3"
        )}
      >
        Your payment is pending...
      </div>
      <div
        className={cn(
          textStyles.body1,
          "widget-payment-pending__message text-blue-dark mb-10"
        )}
      >
        We’ve received your booking request. Your payment is being processed and
        this may take from a few minutes (for card payments) up to 5 business
        days (for bank transfers). Once your payment is confirmed, we’ll update
        your booking and notify you automatically.
      </div>
    </div>
  );
}

const IconLoading = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={120}
    height={120}
    fill="none"
    {...props}
  >
    <circle cx={60} cy={60} r={60} fill="#F3F3F3" />
    <rect width={80} height={60} x={20} y={30} fill="#fff" rx={4} />
    <path
      className="origin-center animate-spin transform-box-fill-box"
      fill="#26A9E0"
      d="M61.5 58v4a1.5 1.5 0 1 1-3 0v-4a1.5 1.5 0 1 1 3 0ZM72 68.5h-4a1.5 1.5 0 1 0 0 3h4a1.5 1.5 0 1 0 0-3Zm-5.282 6.096a1.501 1.501 0 0 0-2.451 1.638c.075.182.186.348.326.487l2.828 2.829a1.503 1.503 0 0 0 2.125-2.125l-2.829-2.829ZM60 76.5a1.5 1.5 0 0 0-1.5 1.5v4a1.5 1.5 0 1 0 3 0v-4a1.5 1.5 0 0 0-1.5-1.5Zm-6.718-1.904-2.828 2.829a1.503 1.503 0 1 0 2.125 2.125l2.828-2.829a1.503 1.503 0 0 0-2.124-2.125ZM53.5 70a1.5 1.5 0 0 0-1.5-1.5h-4a1.5 1.5 0 1 0 0 3h4a1.5 1.5 0 0 0 1.5-1.5Zm-.925-9.546a1.503 1.503 0 1 0-2.125 2.125l2.832 2.825a1.503 1.503 0 0 0 2.126-2.125l-2.833-2.825Z"
    />
    <path fill="#F3F3F3" d="M20 42h80v8H20z" />
  </svg>
);
